import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Title, Text, Flex, Badge, Button } from '@tremor/react';
import type { BatchesResponse } from '../types';

interface BatchesPanelProps {
  data: BatchesResponse | null;
  selectedBatch: string | null;
  onSelectBatch: (batchName: string | null) => void;
  onStartBatch?: () => void;
  onDeleteBatch?: (batchName: string) => void;
}

export function BatchesPanel({ data, selectedBatch, onSelectBatch, onStartBatch, onDeleteBatch }: BatchesPanelProps) {
  const navigate = useNavigate();
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    batchName: string;
  } | null>(null);

  // Dismiss context menu on click-outside or Escape
  useEffect(() => {
    if (!contextMenu) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setContextMenu(null);
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [contextMenu]);

  const handleContextMenu = useCallback((e: React.MouseEvent, batchName: string) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, batchName });
  }, []);

  if (!data) {
    return (
      <Card className="bg-gray-900 ring-gray-800 h-full flex flex-col">
        <Title className="text-gray-100">Batches</Title>
        <div className="mt-4 flex-1 flex items-center justify-center">
          <Text className="text-gray-500">Loading...</Text>
        </div>
      </Card>
    );
  }

  const { batches, totals } = data;

  return (
    <Card className="bg-gray-900 ring-gray-800 h-full flex flex-col">
      <Flex justifyContent="between" alignItems="center">
        <Title className="text-gray-100">Batches</Title>
        {selectedBatch && (
          <Badge color="blue" size="sm">Filtered</Badge>
        )}
      </Flex>

      <div className="mt-4 space-y-1 flex-1 min-h-0 overflow-y-auto">
        {/* ALL option */}
        <BatchRow
          name="ALL"
          documentCount={totals.documentCount}
          completed={totals.completed}
          failed={totals.failed}
          processing={totals.processing}
          skipped={totals.skipped}
          isSelected={selectedBatch === null}
          onClick={() => onSelectBatch(null)}
        />

        {/* Batch list */}
        {batches.map((batch) => (
          <BatchRow
            key={batch.name}
            name={batch.name}
            documentCount={batch.documentCount}
            completed={batch.completed}
            failed={batch.failed}
            processing={batch.processing}
            skipped={batch.skipped}
            isSelected={selectedBatch === batch.name}
            onClick={() => onSelectBatch(batch.name)}
            onContextMenu={(e) => handleContextMenu(e, batch.name)}
          />
        ))}

        {batches.length === 0 && (
          <Text className="text-gray-500 text-sm py-2">No batches found</Text>
        )}
      </div>

      {onStartBatch && (
        <div className="mt-4 pt-3 border-t border-gray-800">
          <Button
            size="xs"
            variant="secondary"
            className="w-full bg-gray-800 border-gray-700 hover:bg-gray-700"
            onClick={onStartBatch}
          >
            Start Batch...
          </Button>
        </div>
      )}

      {/* Context menu */}
      {contextMenu && (
        <div
          className="fixed inset-0 z-50"
          onClick={() => setContextMenu(null)}
          onContextMenu={(e) => { e.preventDefault(); setContextMenu(null); }}
        >
          <div
            className="absolute bg-gray-800 border border-gray-700 rounded-lg shadow-xl py-1 min-w-[160px]"
            style={{ left: contextMenu.x, top: contextMenu.y }}
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/documents?batch=${encodeURIComponent(contextMenu.batchName)}`);
                setContextMenu(null);
              }}
              className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-gray-100 transition-colors"
            >
              View Documents
            </button>
            {!data?.isProduction && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDeleteBatch?.(contextMenu.batchName);
                  setContextMenu(null);
                }}
                className="w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-gray-700 hover:text-red-300 transition-colors"
              >
                Delete Batch...
              </button>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

interface BatchRowProps {
  name: string;
  documentCount: number;
  completed: number;
  failed: number;
  processing: number;
  skipped: number;
  isSelected: boolean;
  onClick: () => void;
  onContextMenu?: (e: React.MouseEvent) => void;
}

function BatchRow({ name, documentCount, completed, failed, processing, skipped, isSelected, onClick, onContextMenu }: BatchRowProps) {
  const inProgress = processing;
  const hasFailures = failed > 0;
  const hasSkipped = skipped > 0;

  return (
    <button
      onClick={onClick}
      onContextMenu={onContextMenu}
      className={`w-full text-left p-2 rounded transition-colors ${
        isSelected
          ? 'bg-blue-900/50 ring-1 ring-blue-500'
          : 'bg-gray-800/50 hover:bg-gray-800'
      }`}
    >
      <Flex justifyContent="between" alignItems="center">
        <div className="flex-1 min-w-0">
          <Text className={`font-medium truncate ${isSelected ? 'text-blue-300' : 'text-gray-200'}`}>
            {name}
          </Text>
        </div>
        <div className="flex items-center gap-2 ml-2">
          {inProgress > 0 && (
            <span className="text-xs text-amber-400">{inProgress} running</span>
          )}
          {hasSkipped && (
            <span className="text-xs text-orange-400">{skipped} skipped</span>
          )}
          {hasFailures && (
            <span className="text-xs text-red-400">{failed} failed</span>
          )}
          <span className="text-xs text-gray-400 min-w-[3rem] text-right">
            {documentCount} docs
          </span>
        </div>
      </Flex>

      {/* Progress bar */}
      <div className="mt-1 h-1 bg-gray-700 rounded-full overflow-hidden">
        <div className="h-full flex">
          {completed > 0 && (
            <div
              className="bg-emerald-500 h-full"
              style={{ width: `${(completed / documentCount) * 100}%` }}
            />
          )}
          {skipped > 0 && (
            <div
              className="bg-orange-500 h-full"
              style={{ width: `${(skipped / documentCount) * 100}%` }}
            />
          )}
          {inProgress > 0 && (
            <div
              className="bg-amber-500 h-full"
              style={{ width: `${(inProgress / documentCount) * 100}%` }}
            />
          )}
          {failed > 0 && (
            <div
              className="bg-red-500 h-full"
              style={{ width: `${(failed / documentCount) * 100}%` }}
            />
          )}
        </div>
      </div>
    </button>
  );
}
